import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@env/environment';

export interface JsonLdRouteData {
    enabled?: boolean;
    name?: string;
    description?: string;
    schemaType?: string | string[];
    image?: string;
    keywords?: string[];
}

interface JsonLdRouteProfile extends JsonLdRouteData {
    path: string;
}

type JsonLdNode = Record<string, unknown>;

const APP_NAME = 'Navizard RTLS';
const ORGANIZATION_NAME = 'Bakelor';
const MANAGED_SCRIPT_ID = 'rtls-json-ld';
const DEFAULT_DESCRIPTION = 'Enhance operations with Navizard RTLS for real-time tracking of assets and personnel, safety workflows, alerts and operational visibility.';
const DEFAULT_KEYWORDS = [
    'RTLS',
    'real-time tracking',
    'asset tracking',
    'personnel tracking',
    'location intelligence',
    'operational safety',
    'Navizard'
];

@Injectable({
    providedIn: 'root'
})
export class JsonLdService {
    private readonly routeProfiles: JsonLdRouteProfile[] = [
        {
            path: '/landing',
            name: APP_NAME,
            description: 'Enterprise RTLS platform for live asset tracking, workforce visibility, process intelligence and operational safety.',
            schemaType: 'WebPage'
        },
        {
            path: '/auth/reset-password',
            name: 'Reset Password',
            description: 'Secure Navizard RTLS password reset page.',
            schemaType: 'WebPage'
        },
        {
            path: '/auth/forgot-password',
            name: 'Forgot Password',
            description: 'Navizard RTLS account recovery page.',
            schemaType: 'WebPage'
        },
        {
            path: '/auth',
            name: 'Sign In',
            description: 'Secure sign-in page for Navizard RTLS.',
            schemaType: 'WebPage'
        },
        {
            path: '/setup',
            name: 'Navizard RTLS Setup',
            description: 'Initial setup workflow for configuring Navizard RTLS.',
            schemaType: 'WebPage'
        },
        {
            path: '/live-maplibre',
            name: 'Live RTLS Map',
            description: 'Live map view for real-time asset and personnel tracking in Navizard RTLS.',
            schemaType: 'WebPage'
        },
        {
            path: '/live',
            name: 'Live RTLS Tracking',
            description: 'Real-time location monitoring for assets, tags, readers and personnel.',
            schemaType: 'WebPage'
        },
        {
            path: '/dashboards',
            name: 'Operations Dashboard',
            description: 'Navizard RTLS dashboards for operational monitoring, analytics and live status visibility.',
            schemaType: 'WebPage'
        },
        {
            path: '/reports',
            name: 'RTLS Reports',
            description: 'Reporting workspace for movement, presence, access, activity and asset analytics.',
            schemaType: 'CollectionPage'
        },
        {
            path: '/apps',
            name: 'Navizard Apps',
            description: 'Navizard app catalog and installed application workspace.',
            schemaType: 'CollectionPage'
        },
        {
            path: '/notifications',
            name: 'Notifications',
            description: 'Notification preferences, alerts and delivery settings for Navizard RTLS.',
            schemaType: 'WebPage'
        },
        {
            path: '/navizard-search',
            name: 'Navizard Search',
            description: 'Search workspace for Navizard RTLS records and operational data.',
            schemaType: 'SearchResultsPage'
        },
        {
            path: '/screensaver',
            name: 'RTLS Screensaver',
            description: 'Ambient live status display for Navizard RTLS.',
            schemaType: 'WebPage'
        }
    ];

    private pendingUpdate?: ReturnType<typeof setTimeout>;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly router: Router,
        private readonly title: Title
    ) { }

    scheduleUpdate(url: string = this.router.url): void {
        if (this.pendingUpdate) {
            clearTimeout(this.pendingUpdate);
        }

        this.pendingUpdate = setTimeout(() => {
            this.pendingUpdate = undefined;
            this.update(url);
        }, 0);
    }

    update(url: string = this.router.url): void {
        const requestedPath = this.normalizePath(url);
        const canonicalPath = this.normalizeCanonicalPath(requestedPath);
        const routeData = this.collectRouteData();

        if (routeData.enabled === false) {
            this.removeManagedScript();
            return;
        }

        const pageProfile = this.resolvePageProfile(requestedPath, canonicalPath, routeData);
        const canonicalUrl = this.resolveUrl(canonicalPath);
        const siteUrl = this.resolveUrl('/');
        const logoUrl = this.resolveUrl('/assets/images/logo/navizard.png');
        const imageUrl = this.resolveUrl(pageProfile.image || '/assets/favicon/android-icon-192x192.png');
        const breadcrumb = this.createBreadcrumbList(canonicalPath, canonicalUrl);
        const graph = [
            this.createOrganization(siteUrl, logoUrl),
            this.createWebSite(siteUrl),
            this.createSoftwareApplication(siteUrl, imageUrl),
            this.createWebPage(canonicalUrl, pageProfile, imageUrl, !!breadcrumb)
        ];

        if (breadcrumb) {
            graph.push(breadcrumb);
        }

        this.writeGraph({
            '@context': 'https://schema.org',
            '@graph': graph
        });
    }

    private collectRouteData(): JsonLdRouteData {
        let snapshot = this.router.routerState.snapshot.root;
        const merged: JsonLdRouteData = {};

        while (snapshot) {
            const routeData = snapshot.data?.structuredData as JsonLdRouteData | false | undefined;

            if (routeData === false) {
                merged.enabled = false;
            } else if (routeData) {
                Object.assign(merged, routeData);
            }

            snapshot = snapshot.firstChild;
        }

        return merged;
    }

    private resolvePageProfile(requestedPath: string, canonicalPath: string, routeData: JsonLdRouteData): JsonLdRouteData {
        const profile = this.findRouteProfile(requestedPath) || this.findRouteProfile(canonicalPath);
        const fallbackName = this.getFallbackPageName(canonicalPath);

        return {
            schemaType: 'WebPage',
            ...profile,
            ...routeData,
            name: routeData.name || profile?.name || fallbackName,
            description: routeData.description || profile?.description || this.getMetaContent('description') || DEFAULT_DESCRIPTION,
            keywords: routeData.keywords || profile?.keywords || DEFAULT_KEYWORDS
        };
    }

    private findRouteProfile(path: string): JsonLdRouteProfile | undefined {
        return this.routeProfiles
            .filter((profile) => path === profile.path || path.startsWith(`${profile.path}/`))
            .sort((left, right) => right.path.length - left.path.length)[0];
    }

    private createOrganization(siteUrl: string, logoUrl: string): JsonLdNode {
        return this.compact({
            '@type': 'Organization',
            '@id': `${siteUrl}#organization`,
            name: ORGANIZATION_NAME,
            url: siteUrl,
            logo: {
                '@type': 'ImageObject',
                url: logoUrl
            }
        });
    }

    private createWebSite(siteUrl: string): JsonLdNode {
        return this.compact({
            '@type': 'WebSite',
            '@id': `${siteUrl}#website`,
            name: APP_NAME,
            url: siteUrl,
            inLanguage: this.getLanguage(),
            publisher: { '@id': `${siteUrl}#organization` }
        });
    }

    private createSoftwareApplication(siteUrl: string, imageUrl: string): JsonLdNode {
        return this.compact({
            '@type': 'SoftwareApplication',
            '@id': `${siteUrl}#software`,
            name: APP_NAME,
            url: siteUrl,
            image: imageUrl,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            softwareVersion: environment.version,
            description: DEFAULT_DESCRIPTION,
            featureList: [
                'Real-time asset tracking',
                'Personnel location visibility',
                'Live map monitoring',
                'Operational dashboards',
                'Reports and analytics',
                'Alerts and notifications',
                'Access and safety workflows'
            ],
            provider: { '@id': `${siteUrl}#organization` }
        });
    }

    private createWebPage(canonicalUrl: string, pageProfile: JsonLdRouteData, imageUrl: string, hasBreadcrumb: boolean): JsonLdNode {
        const siteUrl = this.resolveUrl('/');
        const schemaType = pageProfile.schemaType || 'WebPage';

        return this.compact({
            '@type': schemaType,
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: pageProfile.name,
            description: pageProfile.description,
            keywords: pageProfile.keywords,
            inLanguage: this.getLanguage(),
            isPartOf: { '@id': `${siteUrl}#website` },
            about: { '@id': `${siteUrl}#software` },
            primaryImageOfPage: {
                '@type': 'ImageObject',
                url: imageUrl
            },
            breadcrumb: hasBreadcrumb ? { '@id': `${canonicalUrl}#breadcrumb` } : undefined
        });
    }

    private createBreadcrumbList(path: string, canonicalUrl: string): JsonLdNode | null {
        const segments = path.split('/').filter(Boolean);

        if (segments.length === 0) {
            return null;
        }

        const itemListElement = segments.map((segment, index) => {
            const itemPath = `/${segments.slice(0, index + 1).join('/')}`;

            return {
                '@type': 'ListItem',
                position: index + 1,
                name: this.humanizeSegment(segment),
                item: this.resolveUrl(itemPath)
            };
        });

        return {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl}#breadcrumb`,
            itemListElement
        };
    }

    private writeGraph(graph: JsonLdNode): void {
        const script = this.getOrCreateManagedScript();
        const space = environment.production ? 0 : 2;
        script.textContent = JSON.stringify(graph, null, space);
    }

    private getOrCreateManagedScript(): HTMLScriptElement {
        const existing = this.document.getElementById(MANAGED_SCRIPT_ID);

        if (existing instanceof HTMLScriptElement) {
            return existing;
        }

        const script = this.document.createElement('script');
        script.id = MANAGED_SCRIPT_ID;
        script.type = 'application/ld+json';
        this.document.head.appendChild(script);
        return script;
    }

    private removeManagedScript(): void {
        this.document.getElementById(MANAGED_SCRIPT_ID)?.remove();
    }

    private getFallbackPageName(path: string): string {
        const title = this.cleanDocumentTitle(this.title.getTitle());

        if (title && !title.includes('.') && title.toLowerCase() !== 'bakelor') {
            return title;
        }

        if (path === '/' || path === '') {
            return APP_NAME;
        }

        return this.humanizeSegment(path.split('/').filter(Boolean).join(' '));
    }

    private cleanDocumentTitle(title: string): string {
        return (title || '')
            .replace(/^\(\d+\)\s+/, '')
            .replace(/\s+-\s+Navizard\s*$/i, '')
            .replace(/^Bakelor\s+-\s+/i, '')
            .trim();
    }

    private getMetaContent(name: string): string {
        const meta = this.document.head.querySelector(`meta[name="${name}"]`);
        return meta?.getAttribute('content')?.trim() || '';
    }

    private getLanguage(): string {
        return this.document.documentElement.lang || 'tr';
    }

    private normalizePath(url: string): string {
        const source = (url || '').trim();
        const path = source.split('?')[0].split('#')[0].trim();
        return path.startsWith('/') ? path || '/' : `/${path}`;
    }

    private normalizeCanonicalPath(path: string): string {
        if (path.startsWith('/auth/reset-password/')) {
            return '/auth/reset-password';
        }

        return path || '/';
    }

    private resolveUrl(path: string): string {
        const origin = this.getOrigin();
        return new URL(path || '/', origin).toString();
    }

    private getOrigin(): string {
        const locationOrigin = this.document.defaultView?.location?.origin;

        if (locationOrigin) {
            return locationOrigin;
        }

        return new URL(this.document.baseURI).origin;
    }

    private humanizeSegment(segment: string): string {
        return segment
            .replace(/[-_]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    private compact<T extends JsonLdNode>(value: T): T {
        Object.keys(value).forEach((key) => {
            const item = value[key];

            if (item === undefined || item === null || (Array.isArray(item) && item.length === 0)) {
                delete value[key];
            }
        });

        return value;
    }
}
